import * as ng from 'angular';
import * as _ from 'underscore';

import {UsersApi, User} from '.';
import {FilterService, FilterServiceFactory, DataList, UtilsService} from '../../shared';

const detailsViewUrl = require('./edit-details-view.html');
const assignmentsViewUrl = require('./edit-assignments-view.html');

export class UsersController {
   static $inject = [
      'userApi', 'usersApi', 'filterServiceFactory', 'utils', 'session',
      'titleService', 'loader', 'dialogs', 'toast'
   ];

   readonly detailsViewUrl = detailsViewUrl;
   readonly assignmentsViewUrl = assignmentsViewUrl;

   constructor(
      private userApi,
      private usersApi:UsersApi,
      filterServiceFactory:FilterServiceFactory,
      private utils:UtilsService,
      private session,
      titleService,
      private loader,
      private dialogs,
      private toast
   ) {
      titleService.setTitle('Users');

      this.filter = filterServiceFactory({
         key: 'users',
         defaults: {
            orgId: this.session.getOrg().id
         },
         properties: ['search', 'brandId', 'campaignId'],
         callback: (property) => this.onFilterChange(property)
      });

      this.orgs = ng.copy(this.session.getOrgs());
      this.orgs.unshift({
         name: '- Any Organization -',
         id: 0
      });

      this.brands = this.session.getBrandsByOrg(this.filter['orgId']);
      this.campaigns = this.session.getCampaignsByBrand(this.filter['brandId']);
      this.disabled.brand = this.brands.length === 0;
      this.disabled.campaign = this.campaigns.length === 0;

      this.refresh();
   }

   filter:FilterService;
   orgs:any[];
   brands:any[];
   campaigns:any[];
   users = new DataList<User, User>();

   activeTab = 0;
   isEditMode = false;
   isAddMode = false;
   model:any;
   disabled = {
      brand: false,
      campaign: false
   };
   accessTypes = User.getPermissableUserTypes(this.session.getUser().role);

   refresh() {
      this.loader.showSpinner();

      // copy filters for mutation
      let inputs = ng.copy(this.filter.inputs);

      // remove org ID filter for the "all" options
      if (inputs['orgId'] === 0) {
         delete inputs['orgId'];
      }

      return this.usersApi.list(inputs).then(users => {
         this.users.setList(users, users.length);
         this.users.checkedIds = [];
      }).finally(() => this.loader.hide());
   }

   onFilterChange(property:string) {
      if (property == 'orgId') {
         this.filter.set('brandId', null);

         if (this.filter['orgId'] === 0) {
            this.disabled.brand = true;
            this.disabled.campaign = true;
         } else {
            this.brands = this.session.getBrandsByOrg(this.filter['orgId']);
            this.disabled.brand = false;
            this.disabled.campaign = true;
         }
      } else if (property == 'brandId') {
         this.filter.set('campaignId', null);

         if (!this.filter['brandId']) {
            this.campaigns = [];
            this.disabled.campaign = true;
         } else {
            this.campaigns = this.session.getCampaignsByBrand(this.filter['brandId']);
            this.disabled.campaign = false;
         }
      }

      this.refresh();
   }

   onModelOrgIdChange() {
      this.model.brands = this.session.getBrandsByOrg(this.model.orgId);
      this.model.brandIds = [];
      this.model.campaignIds = [];
   }

   getOrgName(id:number) {
      return this.session.getOrg(id).name;
   }

   brandHasUserCampaign(brand) {
      if (!ng.isArray(this.model.campaignIds)) {
         return false;
      }
      return _.any(brand.campaigns as any[], (c) => {
         return this.model.campaignIds.indexOf(c.id) > -1;
      });
   }

   select(user:User, editMode?:boolean, $event?:MouseEvent) {
      if ($event) {
         $event.stopPropagation();
      }

      this.isAddMode = false;
      this.isEditMode = !!editMode;

      this.users.selected = user;

      this.model = new User(user);
      this.model.brands = this.session.getOrg(user.orgId).brands;
   }

   collapse() {
      this.isAddMode = false;
      this.isEditMode = false;
      this.users.selected = null;
   }

   cancel() {
      if (this.isAddMode) {
         this.collapse();
      } else {
         this.select(this.users.selected, false);
      }
   }

   add() {
      this.isEditMode = true;
      this.isAddMode = true;
      this.model = new User();
      this.model.orgId = this.filter['orgId'] || this.session.getOrg().id;
      this.model.brands = this.session.getBrandsByOrg(this.model.orgId);
   }

   next() {
      if (this.isEditMode && !this.isAddMode) {
         this.saveEdit();
      } else if (this.isAddMode) {
         this.activeTab = 1;
      }
   }

   save() {
      if (this.isEditMode && !this.isAddMode) {
         this.saveEdit();
      } else if (this.isAddMode) {
         this.saveAdd();
      }
   }

   reset() {
      //reset filters
      this.filter.reset();

      //make sure brand list gets reset
      this.onFilterChange('orgId');

   }

   private saveEdit() {
      let user = this.model;

      this.utils.nullifyBlankFields(user);

      if (!this.confirmPasswordCheck(user)) {
         return;
      }

      this.usersApi.update(user).then(() => {
         // just incase user is editing themselves refresh session user object
         this.userApi.get().then((data) => this.session.setUser(data));

         this.refresh().then(() => {
            let selectedOrgId = this.filter['orgId'];
            if (selectedOrgId !== 0 && selectedOrgId !== user.orgId) {
               this.collapse();
               this.toast.success()
                  .title('User updated successfully')
                  .message("The user's organization was changed, you will need to modify your filters to find them again.")
                  .send();
            } else {
               this.select(user, false);
               this.toast.success().message('User updated successfully').send();
            }
         });
      }).catch((err) => {
         this.toast.error()
            .title('Error updating user')
            .formErrors(err)
            .send();
      });
   };

   private saveAdd() {
      let user = this.model;

      if (!user.role) {
         this.toast.error()
            .title('Error adding user')
            .message('<div><i class="fa fa-exclamation-triangle"></i> Enter an organization and access level</div>')
            .send();
         return;
      }

      this.utils.nullifyBlankFields(user);

      if (!this.confirmPasswordCheck(user)) {
         return;
      }

      this.usersApi.create(user).then(() => {
         this.toast.success().message('User created successfully').send();
         this.refresh().then(() => this.select(user, false));
      }).catch((err) => {
         this.toast.error()
            .title('Error adding user')
            .formErrors(err)
            .send();
      });
   };

   private confirmPasswordCheck(user) {
      if (user.password) {
         if (user.password != user.confirmPassword) {
            this.toast.error()
            .title('Invalid Password')
            .message('Password and Confirm Password do not match.')
            .send();

            this.activeTab = 0;

            return false;
         }
      }

      return true;
   }

   remove(user, $event:MouseEvent) {
      $event.stopPropagation();

      let message = `Are you sure you want to delete the user ${user.handle}?`;
      this.dialogs.confirm(message).then(() => {
         this.loader.showSpinner();

         this.usersApi.del(user.id).then(() => {
            this.toast.message(`User "${user.handle}" was deleted`).send();
            this.refresh();
         });
      });
   }

   removeBulk() {
      let message = `Are you sure you want to delete ${this.users.checkedCount} users?`;
      this.dialogs.confirm(message).then(() => {
         this.loader.showSpinner();

         this.usersApi.del(this.users.checkedIds).then(() => {
            this.toast.message(`${this.users.checkedCount} users were deleted`).send();
            this.refresh();
         });
      });
   }

   unlockUser() {
      let user = this.model;
      let message = `Are you sure you want to unlock ${user.handle}?`;
      this.dialogs.confirm(message).then(() => {
         this.loader.showSpinner();

         this.usersApi.unlock(user.id).then(() => {
            this.toast.message(`${user.handle} has been unlocked`).send();
            this.refresh();
         });
      });
   }
}

ng.module('e360')
   .controller('UsersController', UsersController);
